import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Spinner } from '../../../../common';
import HelpForm from './HelpForm/HelpForm.jsx';
import { routes } from '../../../../../utils/helpers/ApiClient';
import {
  useGetHelpItemQuery,
  useSaveHelpMutation,

} from '../../../../../redux/services/help';
import { useGetRolesQuery } from '../../../../../redux/services/roles';
import { AppBarCMS } from '../../../../basic';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import Form from '../../../../antd/form/Form/Form';

import UnsavedChangesModal from '../../../../common/modals/UnsavedChangesModal/UnsavedChangesModal';
import { useToastMessages } from '../../../../../hooks/useToastMessages.jsx';
import { processUrlSlug } from '../../../../../utils/helpers/urlProcessor.jsx';
import useHelpWithModal from '../../../../../hooks/useHelpWithModal.jsx';
import BaseHtmlModal from '../../../../common/modals/RawHtmlModal/BaseHtmlModal.jsx';
import { isUndefined } from 'lodash';

const HelpEdit = ({ mode }) => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isCreate = mode === 'create';
  const isEdit = mode === 'edit';
  const {
    saveSuccess: showSaveSuccess
  } = useToastMessages()
  const { data: helpDetail, isLoading: isLoadingHelpContent } = useGetHelpItemQuery(id, {
    skip: isUndefined(id) || isCreate,
  });
  const { data: allRoles, isLoading: isLoadingRoles } = useGetRolesQuery();

  const [saveHelp, { isSuccess: saveSuccess }] = useSaveHelpMutation();

  const [filteredRoles, setFilteredRoles] = useState(helpDetail?.roles || []);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [helpTitle, setHelpTitle] = useState(helpDetail?.title || '');
  const [helpContent, setHelpContent] = useState(helpDetail?.content || '');
  const [helpUrl, setHelpUrl] = useState(helpDetail?.url || '');
  const [helpSlug, setHelpSlug] = useState(helpDetail?.slug || '');
  const [changesMade, setChangesMade] = useState(false);
  const [selectedURL, setSelectedURL] = useState(null);

  const {
    helpDetail: helpDetailItem,
    helpActions,
    modal
  } = useHelpWithModal();

  const handleURLChange = (selectedOption) => {
    setSelectedURL(selectedOption);
  };
  const handleSlugChange = (slugValue) => {
    setHelpSlug(slugValue || '');
  };

  const initialRolesCount = useRef(0);
  const initialTitle = useRef('');
  const initialContent = useRef('');
  const initialUrl = useRef('');
  const initialSlug = useRef('');

  const routeArrayOptions = useMemo(() => {
    const uniqueRoutes = new Map();

    for (const [_, value] of Object.entries(routes)) {
      if (typeof value === "string" && value.includes("cms")) {
        uniqueRoutes.set(value, value);
      }
    }

    return Array.from(uniqueRoutes, ([value, label]) => ({
      value,
      label,
    }));
  }, [routes]);


  useEffect(() => {
    if (!isUndefined(helpDetail)) {
      setFilteredRoles(helpDetail.roles);
      setHelpTitle(helpDetail.title);
      setHelpContent(helpDetail.content);
      setHelpSlug(helpDetail.slug || '');

      const foundOption = routeArrayOptions.find(option => option.value === helpDetail?.url);
      if (foundOption) setSelectedURL(foundOption.value);

      if (initialRolesCount.current === 0 && initialTitle.current === '' && initialContent.current === '' && initialUrl.current === '' && initialSlug.current === '') {
        initialRolesCount.current = helpDetail.roles.length;
        initialTitle.current = helpDetail.title;
        initialContent.current = helpDetail.content;
        initialUrl.current = helpDetail.url;
        initialSlug.current = helpDetail.slug || '';
      }
    }
  }, [helpDetail, routeArrayOptions]);

  const handleRoleChange = (selectedRoleIds) => {
    const selectedRoles = allRoles.filter(role => selectedRoleIds.includes(role.id));
    setFilteredRoles(selectedRoles);
  };

  useEffect(() => {
    if (
      initialRolesCount.current !== filteredRoles.length ||
      initialTitle.current !== helpTitle ||
      initialContent.current !== helpContent ||
      initialUrl.current !== helpUrl ||
      initialSlug.current !== helpSlug // Pridané pre slug
    ) {
      setChangesMade(true);
    } else {
      setChangesMade(false);
    }
  }, [filteredRoles, helpTitle, helpContent, helpUrl, helpSlug]); // Pridané helpSlug

  const title = isCreate ? t('Pridanie pomoci') : t('Editovanie pomoci');

  useEffect(() => {
    if (saveSuccess) {
      showSaveSuccess();
      navigate(routes.cmsHelp);
    }
  }, [saveSuccess]);

  if (isLoadingHelpContent || isLoadingRoles ) {
    return <Spinner />;
  }

  return (
    <>
      {isEdit && !isSubmitted && <UnsavedChangesModal changesMade={changesMade} />}
      <AppBarCMS
        title={title}

        backUrl={routes.cmsHelp}
        backUrlTooltip={t('Pomoc')}
        //mainActions={helpActions}
        permissionsBasic={'help'}
      />

      <div style={{ margin: '10px' }}>
        <Form
          key={helpDetail?.id || 'new'}
          initialValues={{
            title: helpTitle,
            content: helpContent,
            url: helpDetail?.slug === 1 ? undefined : selectedURL,
            choosedRolesIds: filteredRoles.map(role => role.id),
            slug: helpDetail?.slug === 1 ? helpDetail?.url : undefined, // Use actual slug if available
            useCustomSlug: helpDetail?.slug === 1
          }}
          onValuesChange={(changedValues) => {
            if (changedValues.title !== undefined) setHelpTitle(changedValues.title);
            if (changedValues.content !== undefined) setHelpContent(changedValues.content);
            if (changedValues.url !== undefined) setHelpUrl(changedValues.url);
            if (changedValues.slug !== undefined) setHelpSlug(changedValues.slug || null);
            if(changedValues.roles !== undefined) setFilteredRoles(changedValues.roles);
          }}
          onFinish={values => {
            let roleIds = filteredRoles.map(item => item.id);
            let body;
            if(values.slug) {
              body = {
                title: values.title,
                content: values.content,
                url: processUrlSlug(values.slug),
                roles_ids: roleIds,
                slug: true
              };
            } else if(values.url) {
              body = {
                title: values.title,
                content: values.content,
                url: values.url,
                roles_ids: roleIds,
                slug: false
              };
            }
            setIsSubmitted(true);
            saveHelp({ body, id });
          }}
        >
          <HelpForm
            roles={allRoles || []}
            changesMade={changesMade}
            onRoleChange={handleRoleChange}
            routesArray={routeArrayOptions}
            onURLChange={handleURLChange}
            onSlugChange={handleSlugChange}
          />
        </Form>
      </div>
     {/* {helpDetailItem.data && !helpDetailItem.error && (
        <BaseHtmlModal
          visible={modal.props.visible}
          headerTitle={modal.props.headerTitle}
          content={modal.props.content}
          toggle={modal.props.toggle}
        />
      )}*/}
    </>
  );
};

export default HelpEdit;
